import { lazy } from 'react'
import { createBrowserRouter, Navigate, ScrollRestoration, useMatches } from 'react-router-dom'
import { LocaleGate } from '@/lib/locale'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { useLocale } from '@/lib/useLocale'
import { detectedLocale } from '@/lib/i18n'
import { PublicLayout, AppLayout, AdminLayout, dashboardHomePath } from '@/components/layouts'
import { RequireApproved, RequireAdmin } from '@/lib/guards'
import { RouteError } from '@/components/RouteError'
import { useAuth } from '@/lib/auth'

/**
 * Per-route tab titles: each route declares `handle.title` (an i18n key) and
 * this component — mounted once beside the locale gate — applies the deepest
 * match. Pages stay free of per-file title plumbing.
 */
function TitleSync() {
  const matches = useMatches()
  const deepest = [...matches]
    .reverse()
    .find((m) => Boolean((m.handle as { title?: string } | undefined)?.title))
  const handle = deepest?.handle as { title?: string } | undefined
  useDocumentTitle(handle?.title ?? null)
  return null
}

/**
 * Root entry point and non-localized catch-all land here. Sends the visitor to
 * their detected locale (cached choice → device language → English) rather than
 * a hardcoded `/es`, so the device language is honored on first visit.
 */
function RedirectToLocale() {
  return <Navigate to={`/${detectedLocale()}`} replace />
}

/**
 * Landing gate: an authenticated, approved user never sees the public landing
 * (their logo / refresh / direct nav to `/` bounces to their own dashboard
 * home). Unauthenticated visitors see the teaser landing. We only redirect
 * once certain (`session && status === 'approved'`) so logged-out visitors
 * render instantly with no loader flash.
 */
function LandingRoute() {
  const { session, profile } = useAuth()
  const locale = useLocale()
  if (session && profile?.status === 'approved')
    return <Navigate to={dashboardHomePath(profile?.role, locale)} replace />
  return <Landing />
}

/**
 * `/dashboard` index: the two registered-user types are independent.
 * Property managers (owner/admin/superadmin) land on their PMS listings;
 * every booker role (company/autónomo/agency/employee) lands on the search
 * dashboard — the properties + map browse view. No shared "Resumen" page.
 */
function DashboardHome() {
  const { profile } = useAuth()
  const locale = useLocale()
  if (profile?.role === 'owner' || profile?.role === 'admin' || profile?.role === 'superadmin')
    return <Navigate to={`/${locale}/dashboard/revenue`} replace />
  return <Browse bare defaultCity="Madrid" />
}

// Route pages are code-split into separate chunks so the initial bundle only
// carries the shell (router/providers/layouts). Leaflet, Supabase and the
// vendor libs are split off into their own chunks via vite manualChunks.
const Landing = lazy(() => import('@/features/landing/Landing').then((m) => ({ default: m.Landing })))
const Register = lazy(() => import('@/features/auth/AuthScreens').then((m) => ({ default: m.Register })))
const Login = lazy(() => import('@/features/auth/AuthScreens').then((m) => ({ default: m.Login })))
const Pending = lazy(() => import('@/features/auth/AuthScreens').then((m) => ({ default: m.Pending })))
const Rejected = lazy(() => import('@/features/auth/AuthScreens').then((m) => ({ default: m.Rejected })))
const Profile = lazy(() => import('@/features/dashboard/Profile').then((m) => ({ default: m.Profile })))
const Bookings = lazy(() => import('@/features/company/Bookings').then((m) => ({ default: m.Bookings })))
const BookingDetail = lazy(() => import('@/features/company/BookingDetail').then((m) => ({ default: m.BookingDetail })))
const Team = lazy(() => import('@/features/company/Team').then((m) => ({ default: m.Team })))
const AgencyClients = lazy(() => import('@/features/company/AgencyClients').then((m) => ({ default: m.AgencyClients })))
const InvoicesByRole = lazy(() => import('@/features/owner/OwnerInvoices').then((m) => ({ default: m.InvoicesByRole })))
const OwnerRevenue = lazy(() => import('@/features/owner/Revenue').then((m) => ({ default: m.OwnerRevenue })))
const OwnerProperties = lazy(() => import('@/features/owner/Properties').then((m) => ({ default: m.Properties })))
const OwnerProducts = lazy(() => import('@/features/owner/Products').then((m) => ({ default: m.Products })))
const PropertyEditor = lazy(() => import('@/features/owner/PropertyEditor').then((m) => ({ default: m.PropertyEditor })))
const RateCalendar = lazy(() => import('@/features/owner/RateCalendar').then((m) => ({ default: m.RateCalendar })))
const PmsCalendar = lazy(() => import('@/features/owner/PmsCalendar').then((m) => ({ default: m.PmsCalendar })))
const OwnerFiscalOnboarding = lazy(() => import('@/features/owner/OwnerFiscalOnboarding').then((m) => ({ default: m.OwnerFiscalOnboarding })))
const Approvals = lazy(() => import('@/features/admin/Approvals').then((m) => ({ default: m.Approvals })))
const AdminConsole = lazy(() => import('@/features/admin/AdminConsole').then((m) => ({ default: m.AdminConsole })))
const AdminRegistry = lazy(() => import('@/features/admin/AdminRegistry').then((m) => ({ default: m.AdminRegistry })))
const Browse = lazy(() => import('@/features/search/Browse').then((m) => ({ default: m.Browse })))
const PropertyDetail = lazy(() => import('@/features/property/PropertyDetail').then((m) => ({ default: m.PropertyDetail })))
const Legal = lazy(() => import('@/features/legal/Legal').then((m) => ({ default: m.Legal })))
const NotFound = lazy(() => import('@/features/NotFound').then((m) => ({ default: m.NotFound })))

export const router = createBrowserRouter([
  { path: '/', element: <RedirectToLocale /> },
  {
    path: '/:locale',
    element: (
      <>
        <LocaleGate />
        <TitleSync />
        {/* Scroll to top on navigation, restore position on back — so going
            back from a property detail lands where you were in the list. */}
        <ScrollRestoration />
      </>
    ),
    // A render error in any lazy page shows a recoverable card instead of
    // unmounting the whole tree to the top-level ErrorBoundary.
    errorElement: <RouteError />,
    children: [
      // Public pages share the public header/footer.
      {
        element: <PublicLayout />,
        children: [
          { index: true, element: <LandingRoute />, handle: { title: 'landing.heroTitle' } },
          { path: 'search', element: <Browse />, handle: { title: 'nav.search' } },
          { path: 'p/:id', element: <PropertyDetail /> },
          { path: 'auth/login', element: <Login />, handle: { title: 'auth.loginTitle' } },
          { path: 'auth/register', element: <Register />, handle: { title: 'auth.registerTitle' } },
          { path: 'auth/pending', element: <Pending />, handle: { title: 'auth.pendingTitle' } },
          { path: 'auth/rejected', element: <Rejected />, handle: { title: 'auth.rejectedTitle' } },
          { path: 'legal/:slug', element: <Legal /> },
          // Localized catch-all: keeps the locale prefix + header/footer.
          { path: '*', element: <NotFound />, handle: { title: 'notFound.title' } },
        ],
      },
      // Authenticated + approved area.
      {
        path: 'dashboard',
        element: <RequireApproved />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <DashboardHome /> },
              { path: 'profile', element: <Profile />, handle: { title: 'profile.title' } },
              { path: 'bookings', element: <Bookings />, handle: { title: 'booking.bookingsTitle' } },
              { path: 'bookings/:id', element: <BookingDetail />, handle: { title: 'booking.bookingsTitle' } },
              // Property detail inside the dashboard — logged-in users never drop
              // onto the public /p/:id (PublicLayout header). Cards route here via
              // propertyDetailPath() when the viewer is approved.
              { path: 'p/:id', element: <PropertyDetail /> },
              { path: 'team', element: <Team />, handle: { title: 'team.title' } },
              { path: 'clients', element: <AgencyClients />, handle: { title: 'agency.title' } },
              { path: 'invoices', element: <InvoicesByRole />, handle: { title: 'invoices.title' } },
              {
                path: 'revenue',
                element: <RequireApproved allow={['owner', 'admin', 'superadmin']} />,
                children: [{ index: true, element: <OwnerRevenue />, handle: { title: 'owner.revenueTitle' } }],
              },
              {
                path: 'properties',
                element: <RequireApproved allow={['owner', 'admin', 'superadmin']} />,
                children: [
                  { index: true, element: <OwnerProperties />, handle: { title: 'owner.propertiesTitle' } },
                  { path: 'new', element: <PropertyEditor />, handle: { title: 'owner.newProperty' } },
                  { path: ':id/edit', element: <PropertyEditor />, handle: { title: 'owner.editProperty' } },
                  { path: ':id/calendar', element: <RateCalendar />, handle: { title: 'owner.calendar.title' } },
                  { path: 'pms', element: <PmsCalendar />, handle: { title: 'owner.pms.title' } },
                ],
              },
              // Owner rate-plan + channel-connection manager (iCal import). Top-
              // level owner route — the nav links to /dashboard/products, so it
              // must live here (not nested under /properties) or the link 404s.
              {
                path: 'products',
                element: <RequireApproved allow={['owner', 'admin', 'superadmin']} />,
                children: [{ index: true, element: <OwnerProducts />, handle: { title: 'owner.products.title' } }],
              },
              // Owner VeriFactu onboarding — fiscal issuer data + apoderamiento
              // grant so bitutravel can remit lodging invoices to AEAT.
              {
                path: 'fiscal',
                element: <RequireApproved allow={['owner', 'admin', 'superadmin']} />,
                children: [{ index: true, element: <OwnerFiscalOnboarding />, handle: { title: 'owner.fiscal.title' } }],
              },
            ],
          },
        ],
      },
      // Admin-only area.
      {
        path: 'admin',
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <Navigate to="approvals" replace /> },
              { path: 'approvals', element: <Approvals />, handle: { title: 'admin.approvalsTitle' } },
              { path: 'accounts', element: <AdminConsole tab="accounts" />, handle: { title: 'admin.navAccounts' } },
              { path: 'bookings', element: <AdminConsole tab="bookings" />, handle: { title: 'admin.navBookings' } },
              { path: 'invoices', element: <AdminConsole tab="invoices" />, handle: { title: 'admin.navInvoices' } },
              { path: 'registry', element: <AdminRegistry />, handle: { title: 'admin.navRegistry' } },
            ],
          },
        ],
      },
    ],
  },
  // Non-localized paths (e.g. /foo): bounce to the detected locale root.
  { path: '*', element: <RedirectToLocale /> },
])